import { useFileInfo, useFileSignedUrl } from "@/api/services/global/file";
import { IconChevronLeft, IconChevronRight } from "@/assets/icons";
import { Avatar } from "@/components/elements/Avatar";
import { Button } from "@/components/elements/Button";
import { DATE_TIME_FORMAT_WITH_SECONDS } from "@/constants/datetime";
import { MIME_TYPES } from "@/constants/file";
import { EMPTY_STR } from "@/constants/string";
import { TTalentUploadFile } from "@/features/talent/types/talentForm";
import { background, divider, text } from "@/theme/colors";
import { textEllipsis } from "@/theme/minxin";
import { TExtendFile } from "@/types/file";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { downloadFileFromPath } from "@/utils/export";
import { convertByteToKb } from "@/utils/file";
import { Box, Modal, Stack, styled, Typography, Button as ButtonMui, Divider, Grid } from "@mui/material";
import { FC, useEffect, useMemo } from "react";

type TFileWithPath = { filePath: string; name?: string };
type TFileList = TExtendFile[] | TTalentUploadFile[] | TFileWithPath[];

const PREVIEWABLE_MIME_TYPES = [...MIME_TYPES.IMAGE, ...MIME_TYPES.TIFF, ...MIME_TYPES.PDF];

// Helper to check if a file is viewable based on extension or MIME type
const isFileViewable = (file: TExtendFile | TTalentUploadFile | TFileWithPath | File): boolean => {
  if (file instanceof File) {
    return PREVIEWABLE_MIME_TYPES.includes(file.type);
  }

  const fileName = 'name' in file ? file.name : '';
  const filePath = 'filePath' in file ? file.filePath : '';
  const fullPath = fileName || filePath || '';

  if (!fullPath) return false;

  const extension = fullPath.toLowerCase().split('.').pop() || '';
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'tif', 'tiff'];
  const pdfExtensions = ['pdf'];

  return imageExtensions.includes(extension) || pdfExtensions.includes(extension);
};

type PreviewModalProps = {
  currentIndex: number;
  filePath?: string;
  fileList: TFileList;
  open: boolean;
  setCurrentIndex: (index: number) => void;
  onClose: () => void;
};

export const PreviewModal: FC<PreviewModalProps> = ({
  currentIndex,
  filePath,
  fileList,
  open,
  setCurrentIndex,
  onClose,
}) => {
  // Filter to only show viewable files (images and PDFs)
  const viewableFileList = useMemo(() => {
    return fileList.filter(file => isFileViewable(file));
  }, [fileList]);

  // Map current index to viewable list index
  const viewableIndex = useMemo(() => {
    const currentFile = fileList[currentIndex];
    return viewableFileList.findIndex(file => file === currentFile);
  }, [fileList, currentIndex, viewableFileList]);

  const currentFile = viewableIndex >= 0 ? viewableFileList[viewableIndex] as TFileWithPath | File | undefined : undefined;

  const currentFilePath =
    currentFile && !(currentFile instanceof File) && "filePath" in currentFile ? (currentFile as TFileWithPath).filePath : filePath;
  const { data: currentFileInfo } = useFileInfo(currentFilePath);
  const { data: currentFileSignedUrl } = useFileSignedUrl(currentFilePath);

  // Close modal if current file is not viewable
  useEffect(() => {
    if (viewableIndex < 0 && open) {
      // Current file is not viewable, try to find next viewable file in original list
      const nextViewableIndex = fileList.slice(currentIndex + 1).findIndex(file => isFileViewable(file));
      if (nextViewableIndex >= 0) {
        setCurrentIndex(currentIndex + 1 + nextViewableIndex);
      } else {
        const prevViewableIndex = fileList.slice(0, currentIndex).reverse().findIndex(file => isFileViewable(file));
        if (prevViewableIndex >= 0) {
          setCurrentIndex(currentIndex - 1 - prevViewableIndex);
        } else {
          onClose();
        }
      }
    }
  }, [viewableIndex, open, fileList, currentIndex, setCurrentIndex, onClose]);

  // Calculate if we can navigate within viewable files
  const canGoNext = viewableIndex < viewableFileList.length - 1;
  const canGoPrev = viewableIndex > 0;

  // Enhanced next handler that navigates to next viewable file
  const handleNext = () => {
    if (!canGoNext) return;
    const nextViewableFile = viewableFileList[viewableIndex + 1];
    const nextIndexInOriginalList = fileList.findIndex(file => file === nextViewableFile);
    if (nextIndexInOriginalList >= 0) {
      setCurrentIndex(nextIndexInOriginalList);
    }
  };

  // Enhanced prev handler that navigates to previous viewable file
  const handlePrev = () => {
    if (!canGoPrev) return;
    const prevViewableFile = viewableFileList[viewableIndex - 1];
    const prevIndexInOriginalList = fileList.findIndex(file => file === prevViewableFile);
    if (prevIndexInOriginalList >= 0) {
      setCurrentIndex(prevIndexInOriginalList);
    }
  };

  const handleDownloadFile = async () => {
    try {
      if (currentFilePath) {
        await downloadFileFromPath(currentFilePath);
      }
    } catch (error) {
      showError(error);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContainer>
        <Stack>
          <Stack gap={2}>
            <FileName variant="sub16Bold" color={text.secondary}>
              {(currentFile instanceof File ? currentFile.name : (currentFile as TFileWithPath)?.name) ?? currentFileInfo?.data?.name}
            </FileName>
            {currentFileInfo && (MIME_TYPES.IMAGE.includes(currentFileInfo.data.mimeType) || MIME_TYPES.TIFF.includes(currentFileInfo.data.mimeType)) ? (
              <Avatar filePath={currentFilePath} width={800} height={500} />
            ) : (
              <iframe
                src={currentFileSignedUrl?.data.signedUrl}
                style={{ marginBottom: "20px", height: 500, width: 800, border: "none" }}
                title="PDF Page"
                allowFullScreen
              />
            )}
          </Stack>
          <Stack pt={2} gap={0.5}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Typography variant="body14" color={text.secondary}>
                  更新日時
                </Typography>
                <Divider />
                <Typography variant="body14">
                  {currentFileInfo?.data.updatedAt
                    ? dayjs(currentFileInfo?.data.updatedAt).format(DATE_TIME_FORMAT_WITH_SECONDS)
                    : EMPTY_STR.DATE_TIME}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body14" color={text.secondary}>
                  ファイル容量
                </Typography>
                <Divider />
                <Typography variant="body14">{convertByteToKb(currentFileInfo?.data?.size ?? 0)}kb</Typography>
              </Grid>
            </Grid>
          </Stack>
          <Stack py={2} flexDirection="row" gap={2} justifyContent="space-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
            >
              キャンセル
            </Button>
            <Button variant="primary" size="sm" onClick={handleDownloadFile}>
              ダウンロード
            </Button>
          </Stack>
          {viewableFileList.length > 1 && (
            <Stack pt={2} flexDirection="row" gap={2} justifyContent="center" alignItems="center">
              <BoxIcon
                onClick={handlePrev}
                disabled={!canGoPrev}
                style={{ opacity: canGoPrev ? 1 : 0.4, cursor: canGoPrev ? "pointer" : "not-allowed" }}
              >
                <IconChevronLeft width={20} height={20} color={text.primary} />
              </BoxIcon>
              {/* <Typography variant="body14">{`${currentIndex + 1} / ${fileList?.length}`}</Typography> */}
              <BoxIcon
                onClick={handleNext}
                disabled={!canGoNext}
                style={{ opacity: canGoNext ? 1 : 0.4, cursor: canGoNext ? "pointer" : "not-allowed" }}
              >
                <IconChevronRight width={20} height={20} color={text.primary} />
              </BoxIcon>
            </Stack>
          )}
        </Stack>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  min-width: 348px;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  padding: 24px;
  max-height: 90vh;
`;

const FileName = styled(Typography)`
  max-width: 300px;
  ${textEllipsis};
  flex: 1;
`;

const BoxIcon = styled(ButtonMui)`
  padding: 0;
  min-width: 24px;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid ${divider.middle};
  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;
